/**
 * SB5-B.2 — Add Contract Position modal.
 *
 * Source: `03-page-contract-position.md` §"Add Modal".
 *
 * Cascading single-select trio:
 *   1. Project picker — required. Options: projects in states OFFER,
 *      DATA_IMPORT, DATA_VALIDATION_CUSTOMER, PREPARATION, EXECUTION,
 *      ACCOUNTING per the canonical ProjectState enum (Q14).
 *   2. Contract picker — required, enabled once a project is picked.
 *      Options: contracts in the chosen project. Display:
 *      `{contractCode} ({siteName})`.
 *   3. Asset picker — required, enabled once a contract is picked.
 *      Options: assets in the chosen contract's site. Display uses
 *      `composeAssetLabel`. Per Q17 — does NOT filter out
 *      already-linked assets.
 *
 * Cascade reset rule: changing the project resets contract + asset;
 * changing the contract resets asset only.
 *
 * Below the trio: pricing read-only placeholder + externalIds editor.
 *
 * Save fires `POST /contract-positions` with `{contractId, assetId,
 * externalIds?}` per Q15 (pricing not included — read-only placeholder).
 */
import { useEffect, useMemo, useState, type ReactElement } from "react";
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField,
} from "@mui/material";
import { useProjectsListQuery } from "../projects/useProjects.js";
import type { ProjectListItem, ProjectState } from "../projects/types.js";
import {
  composeAssetLabel,
  useAssetsBySiteQuery,
  useContractsByProjectQuery,
  useCreateContractPositionMutation,
} from "./useContractPositions.js";
import {
  ExternalIdsEditor,
  externalIdsRowsToObject,
  type ExternalIdRow,
} from "./ExternalIdsEditor.js";
import { PricingPlaceholder } from "./PricingPlaceholder.js";
import type { ContractListItem } from "../contracts/types.js";
import type { AssetsBySiteRow } from "./useContractPositions.js";
import { XIcon } from "@aeriosiq/ui/icons";

export interface ContractPositionAddModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

/**
 * Per Q14 — these literals MUST match the canonical ProjectState
 * enum in `_BACKEND/src/clients/projects/dto/project-v1.dto.ts`
 * exactly. Verified at write time against PROJECT_STATES in
 * `_FRONTEND/apps/web/src/features/projects/types.ts`. If any drift
 * lands in the canonical enum, drop the missing literal here.
 */
const ALLOWED_PROJECT_STATES: readonly ProjectState[] = [
  "OFFER",
  "DATA_IMPORT",
  "DATA_VALIDATION_CUSTOMER",
  "PREPARATION",
  "EXECUTION",
  "ACCOUNTING",
];

export function ContractPositionAddModal({
  open,
  onClose,
  onCreated,
}: ContractPositionAddModalProps): ReactElement {
  const [projectId, setProjectId] = useState<string | null>(null);
  const [contractId, setContractId] = useState<string | null>(null);
  const [assetId, setAssetId] = useState<string | null>(null);
  const [externalIdsRows, setExternalIdsRows] = useState<ExternalIdRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Reset modal state when it (re-)opens. Without this a stale
  // selection from a cancelled previous session would survive.
  useEffect(() => {
    if (!open) return;
    setProjectId(null);
    setContractId(null);
    setAssetId(null);
    setExternalIdsRows([]);
    setError(null);
  }, [open]);

  const projectsQuery = useProjectsListQuery({
    page: 1,
    pageSize: 200,
    states: ALLOWED_PROJECT_STATES,
    includeCompleted: true, // states[] is explicit; bypass the includeCompleted filter
    sortBy: "projectName",
    sortDir: "asc",
  });
  const contractsQuery = useContractsByProjectQuery(projectId);
  const selectedContract: ContractListItem | null = useMemo(() => {
    if (!contractId) return null;
    return (
      contractsQuery.data?.items.find((c) => c.contractId === contractId) ??
      null
    );
  }, [contractId, contractsQuery.data]);
  const assetsQuery = useAssetsBySiteQuery(selectedContract?.siteId ?? null);

  const createMut = useCreateContractPositionMutation();

  function onPickProject(p: ProjectListItem | null): void {
    setProjectId(p?.projectId ?? null);
    // Cascade reset: changing project clears contract + asset (story
    // §"we do not lock-in").
    setContractId(null);
    setAssetId(null);
  }
  function onPickContract(c: ContractListItem | null): void {
    setContractId(c?.contractId ?? null);
    // Cascade reset: changing contract clears asset.
    setAssetId(null);
  }
  function onPickAsset(a: AssetsBySiteRow | null): void {
    setAssetId(a?.id ?? null);
  }

  const canSave =
    typeof projectId === "string" &&
    typeof contractId === "string" &&
    typeof assetId === "string";

  function handleSave(): void {
    if (!canSave || !contractId || !assetId) return;
    setError(null);
    const externalIdsObj = externalIdsRowsToObject(externalIdsRows);
    const body = {
      contractId,
      assetId,
      ...(Object.keys(externalIdsObj).length > 0
        ? { externalIds: externalIdsObj }
        : {}),
    };
    createMut.mutate(body, {
      onSuccess: () => {
        onCreated();
        onClose();
      },
      onError: (e: unknown) => {
        const msg = e instanceof Error ? e.message : "Create failed";
        setError(msg);
      },
    });
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="contract-position-add-title"
    >
      <DialogTitle id="contract-position-add-title">
        Add Contract Position
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <XIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <Autocomplete<ProjectListItem>
            options={projectsQuery.data?.items ?? []}
            value={
              projectsQuery.data?.items.find(
                (p) => p.projectId === projectId,
              ) ?? null
            }
            onChange={(_e, next) => {
              onPickProject(next);
            }}
            getOptionLabel={(o) => o.projectName}
            isOptionEqualToValue={(a, b) => a.projectId === b.projectId}
            loading={projectsQuery.isPending}
            renderInput={(p) => {
              const tfProps = p as React.ComponentProps<typeof TextField>;
              return (
                <TextField
                  {...tfProps}
                  label="Project"
                  required
                  slotProps={{
                    inputLabel: { shrink: true },
                    htmlInput: {
                      ...p.inputProps,
                      "aria-label": "Project",
                    },
                  }}
                />
              );
            }}
          />
          <Autocomplete<ContractListItem>
            // Contract picker (cascading on project).
            options={contractsQuery.data?.items ?? []}
            value={selectedContract}
            onChange={(_e, next) => {
              onPickContract(next);
            }}
            disabled={projectId === null}
            getOptionLabel={(o) =>
              `${String(o.contractCode)} (${o.siteName ?? "—"})`
            }
            isOptionEqualToValue={(a, b) => a.contractId === b.contractId}
            loading={contractsQuery.isPending}
            renderInput={(p) => {
              const tfProps = p as React.ComponentProps<typeof TextField>;
              return (
                <TextField
                  {...tfProps}
                  label="Contract"
                  required
                  slotProps={{
                    inputLabel: { shrink: true },
                    htmlInput: {
                      ...p.inputProps,
                      "aria-label": "Contract",
                    },
                  }}
                />
              );
            }}
          />
          <Autocomplete<AssetsBySiteRow>
            options={assetsQuery.data?.items ?? []}
            value={
              assetsQuery.data?.items.find((a) => a.id === assetId) ?? null
            }
            onChange={(_e, next) => {
              onPickAsset(next);
            }}
            disabled={contractId === null}
            getOptionLabel={composeAssetLabel}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            loading={assetsQuery.isPending}
            renderInput={(p) => {
              const tfProps = p as React.ComponentProps<typeof TextField>;
              return (
                <TextField
                  {...tfProps}
                  label="Asset"
                  required
                  slotProps={{
                    inputLabel: { shrink: true },
                    htmlInput: {
                      ...p.inputProps,
                      "aria-label": "Asset",
                    },
                  }}
                />
              );
            }}
          />
          <Divider />
          <PricingPlaceholder />
          <ExternalIdsEditor
            value={externalIdsRows}
            onChange={setExternalIdsRows}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="text">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!canSave || createMut.isPending}
          aria-label="Save new contract position"
        >
          {createMut.isPending ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
